import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { removeToken, saveToken } from '@utils/tokenUtils';
import { detectMobile } from '@utils/detectMobile';
import { Input } from '@inputs/Input/Input';
import { InputCheckbox } from '@inputs/InputCheckbox';
import { Button } from '@components/Core/Button';
import { IFinishRegistrationFormFields, FinishRegistrationFormTransFields } from './types';
import { finishRegistrationFormTranslations } from './translations';
import { AuthTransFields } from '@/pages/Auth/types';
import { authTranslations } from '@/pages/Auth/translations';

export const FinishRegistrationForm: FC = observer(() => {
  const {
    authStore: { setAuthStep, token, setToken, removeProfile },
    profileStore: { user, changeProfile },
  } = useStore();

  const { t } = useMyTranslations<FinishRegistrationFormTransFields | AuthTransFields>([
    finishRegistrationFormTranslations.name,
    authTranslations.name,
  ]);

  const isMob = detectMobile();

  const { mutate, isPending } = useMutation({
    mutationFn: changeProfile,
    mutationKey: ['changeProfile'],
    onSuccess: () => {
      setToken(null);
      setAuthStep('creds');
    },
    onError: () => {
      removeProfile();
    },
  });

  const useFormMethods = useForm<IFinishRegistrationFormFields>({
    defaultValues: { name: user?.name, isAgreedPolicy: user?.isAgreedPolicy },
  });

  const { handleSubmit, watch } = useFormMethods;

  const onSubmit = (data: IFinishRegistrationFormFields) => {
    if (token) saveToken(token);
    mutate(data);
  };

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <FormProvider {...useFormMethods}>
      <form className="flex flex-col gap-y-[24px]">
        <Input
          name="name"
          type="text"
          placeholder={t('Name Placeholder')}
          required
          // label={isMob ? t('Complete Registration', { ns: authTranslations.name }) : undefined}
          label={t('Name label')}
          showRequiredIcon
        />
        <div className="flex gap-x-[4px] gap-y-[8px] flex-wrap">
          <InputCheckbox name="isAgreedPolicy" inputText={t('I agree to Terms of')} required />
          <a href="https://geobuyer.app/agreement" className="small-text underline" target="_blank">
            {t('Service and Privacy Policy')}
          </a>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          text="Continue"
          disabled={isPending || !watch('name') || !watch('isAgreedPolicy')}
        />
      </form>
    </FormProvider>
  );
});
